<template>
  <div>
    <!-- Primary actions first in DOM so reading order matches intent (WCAG 1.3.2); modal last -->
    <div class="table-actions">
      <div v-if="isForAcknowledge">
        <button
          class="btn btn-comfirm w-100 mb-2"
          @click="acknowledgeAppointmentPass(pass?.id)"
        >
          Acknowledge
        </button>
      </div>
      <div v-if="isForConfirm" data-test-id="apt-pass-confirm-btn">
        <button
          class="btn btn-comfirm w-100 mb-2"
          @click="confirmAppointmentPass(pass?.id)"
        >
          Confirm
        </button>
      </div>

      <div class="d-flex">
        <button
          v-if="isForEdit"
          class="btn btn-edit me-2"
          @click="editAppointmentPass(pass)"
        >
          <i class="flaticon-edit me-1"></i>
          Edit
        </button>
        <button
          v-if="isForCancel"
          class="btn btn-cancel"
          @click="showCancelModal"
        >
          Cancel
        </button>
        <button
          v-if="isForDelete"
          class="btn btn-cancel ms-1"
          @click="() => (state.modal.isVisible.delete = true)"
        >
          Delete
        </button>
      </div>
    </div>

    <Loader
      :is-processing="state.isLoading"
      classes="position-absolute"
      :teleport-to-body="false"
    />

    <CModal
      backdrop="static"
      v-if="isForCancel"
      :visible="shouldModalBeVisible"
      :close-on-backdrop="false"
    >
      <div
        class="pt-4 px-4 create-popup-header d-flex align-items-center justify-content-end"
      >
        <div class="text-end close-btn">
          <button
            type="button"
            class="close-modal-action btn btn-link p-0 border-0"
            :aria-label="$t('common.close')"
            @click="closeCancelModal"
          >
            <i class="ri-close-line cursor-pointer" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div v-if="pass" class="px-3">
        <h3 class="text-center mb-4">Cancel Pass</h3>
        <AptPassCancelation
          :pass="pass"
          :count-type="countType"
          :is-beyond="isPassBeyond"
        />
      </div>
      <template #footer-wrapper>
        <div></div>
      </template>
    </CModal>
  </div>
</template>

<script>
import { reactive, computed } from "vue"
import { useStore } from "vuex"
import useAppToast from "@/composables/useAppToast"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import helpers from "@/helpers/index.js"
import AptPassCancelation from "@/v3components/Forms/AptPassCancelation.vue"

export default {
  name: "AptPassActionButtons",
  components: {
    Loader,
    AptPassCancelation
  },
  props: {
    pass: {
      type: Object,
      required: true
    },
    countType: {
      type: String,
      default: ""
    },
    isPassBeyond: {
      type: Boolean,
      default: false
    }
  },
  emits: ["editAppPass"],
  setup(props, { emit }) {
    const store = useStore()
    const toast = useAppToast()

    const state = reactive({
      isLoading: false,
      modal: {
        isVisible: {
          delete: false,
          cancel: false
        }
      }
    })

    const isForAcknowledge = computed(() => props?.pass?.is_for_acknowledge)
    const isForConfirm = computed(() => props?.pass?.is_for_confirm)
    const isForCancel = computed(
      () => props?.pass?.can_be_canceled && !props?.pass?.cancel_reason
    )
    const isForDelete = computed(() => props?.pass?.can_be_deleted)
    const isForEdit = computed(() => props?.pass?.can_be_edited)
    const shouldModalBeVisible = computed(
      () => state?.modal?.isVisible?.cancel || false
    )

    const confirmAppointmentPass = async (passID) => {
      state.isLoading = true
      try {
        await store.dispatch("adultAptPass/confirmAppointmentPass", passID)
        incrementTodayCounter()
        decrementCurrentCounter()
        toast.success("Appointment confirmed successfully!")
      } catch {
        toast.error("Failed to confirm appointment. Please try again.")
      } finally {
        state.isLoading = false
      }
    }

    const acknowledgeAppointmentPass = async (passID) => {
      state.isLoading = true
      try {
        await store.dispatch("adultAptPass/acknowledgeAppointmentPass", passID)
        toast.success("Appointment acknowledged successfully!")
      } catch {
        toast.error("Failed to acknowledge appointment. Please try again.")
      } finally {
        state.isLoading = false
      }
    }

    const editAppointmentPass = (pass) => {
      emit("editAppPass", pass)
    }

    const decrementCurrentCounter = () => {
      if (
        ["appointments_future_awaiting", "appointments_awaiting"].includes(
          props?.countType
        )
      ) {
        store.commit("dashboardTable/DECREMENT_STAT", props?.countType)
      }
    }

    const incrementTodayCounter = () => {
      if (
        ["awaitingTodayAppointmentsCount", "appointments_awaiting"].includes(
          props?.countType
        )
      ) {
        store.commit("dashboardTable/INCREMENT_STAT", "todayAppointmentsCount")
      }
    }

    const closeCancelModal = () => {
      state.modal.isVisible.cancel = false
      helpers.setModalToggleClass(false)
    }

    const showCancelModal = () => {
      state.modal.isVisible.cancel = true
      helpers.setModalToggleClass(true)
    }

    return {
      state,
      isForAcknowledge,
      isForConfirm,
      isForCancel,
      isForDelete,
      isForEdit,
      shouldModalBeVisible,
      confirmAppointmentPass,
      acknowledgeAppointmentPass,
      editAppointmentPass,
      decrementCurrentCounter,
      incrementTodayCounter,
      closeCancelModal,
      showCancelModal
    }
  }
}
</script>
